/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState, useRef } from "react";
import {
  retrieveNotificationsSettings,
  saveNotificationsSettings,
} from "../../services/user";
import {
  LocalNotifications,
  LocalNotificationSchema,
} from "@capacitor/local-notifications";
import { PushNotifications } from "@capacitor/push-notifications";
import {
  IonPage,
  IonText,
  IonIcon,
  useIonRouter,
  IonModal,
  IonButton,
  IonContent,
  IonToast,
  isPlatform,
} from "@ionic/react";
import { chevronForward } from "ionicons/icons";
import Header from "../../components/Header";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { getMyFollowing } from "../../services/datashare";
import { useStorage } from "../../contexts/StorageContext";
import { syncTimePickerWithSettings } from "../../utils/date";
import { BleClient } from "@capacitor-community/bluetooth-le";
import {
  createGoalDataFromResponse,
  createSaveData,
  formatTime,
  getDefaultGoalData,
  GoalData,
  ModalType,
  NotificationItem,
  PushNotificationSettings,
  renderCustomModal,
  renderGoalModal,
  TherapySettings,
  User,
} from "./TherapyNotifications";
import { getIonicStorage, ionicStorage } from "../../services/storageService";
import { clearLocalNotifications } from "../../services/localNotifications";

const MyNotifications: React.FC = () => {
  // HOOKS & ROUTER
  const [hrs, setHrs] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [meridian, setMeridian] = useState(0);
  const platform = Capacitor.getPlatform();
  const router = useIonRouter();

  // Modal management states
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [currentModalType, setCurrentModalType] = useState<ModalType>(null);
  const [showDownloadPrompt, setShowDownloadPrompt] = useState(false);
  const [isReminderEnabled, setIsReminderEnabled] = useState(false);

  // Goal states for each notification type
  const [missedDaysGoal, setMissedDaysGoal] = useState<GoalData | null>(null);
  const [pressureThresholdGoal, setPressureThresholdGoal] =
    useState<GoalData | null>(null);

  //Toast management states
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  // User and data states
  const [userId, setUserId] = useState<string | null>(null);
  const [followingList, setFollowingList] = useState<User[]>([]);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [notificationPermission, setNotificationPermission] = useState(false);
  const [pushNotificationSettings, setPushNotificationSettings] =
    useState<PushNotificationSettings>({
      enabled: false,
      deliveryType: "Immediate Delivery",
    });

  const { storage, isStorageReady } = useStorage();

  // FIXED: Add ref for app state listener cleanup
  const appStateListenerRef = useRef<any>(null);

  const [therapySettings, setTherapySettings] = useState<TherapySettings>({
    dailyReminder: {
      time: "",
      enabled: true,
    },
    missedDays: {
      count: 0,
      enabled: true,
    },
    pressureThresholds: {
      value: 0,
      enabled: true,
    },
  });

  // PERMISSION & API FUNCTIONS
  const checkPushPermission = async () => {
    if (Capacitor.isNativePlatform()) {
      try {
        console.log("Checking push notification permissions...");
        const permStatus = await PushNotifications.checkPermissions();
        console.log("Push notification permission status:", permStatus);
        updatePushSettings(
          permStatus.receive === "granted",
          pushNotificationSettings.deliveryType,
          "Check Permission"
        );
        setNotificationPermission(permStatus.receive === "granted");
      } catch (e) {
        updatePushSettings(
          false,
          pushNotificationSettings.deliveryType,
          "Check Permission Catch"
        );
        setNotificationPermission(false);
      }
    } else {
      updatePushSettings(
        false,
        pushNotificationSettings.deliveryType,
        "Check Permission else"
      );
      setNotificationPermission(false);
    }
  };

  const requestNotificationPermission = async () => {
    if (!Capacitor.isNativePlatform()) {
      setShowDownloadPrompt(true);
      return false;
    }

    try {
      // First check current permissions
      const currentPermissions = await PushNotifications.checkPermissions();

      if (currentPermissions.receive === "granted") {
        setNotificationPermission(true);
        updatePushSettings(
          true,
          pushNotificationSettings.deliveryType,
          "Already Granted"
        );
        return true;
      }

      // Check local notifications permission as well
      const localPermissions = await LocalNotifications.checkPermissions();

      if (
        currentPermissions.receive === "denied" ||
        localPermissions.display === "denied"
      ) {
        // Permissions were previously denied - need to go to settings
        setToastMessage(
          "Notification permissions were denied. Please enable them in Settings."
        );
        setShowToast(true);

        // Show modal to guide user to settings
        setTimeout(() => {
          navigateToSystemSettings();
        }, 2000);

        return false;
      }

      // Request permissions
      const pushResult = await PushNotifications.requestPermissions();
      const localResult = await LocalNotifications.requestPermissions();

      const isGranted =
        pushResult.receive === "granted" && localResult.display === "granted";

      if (isGranted) {
        setNotificationPermission(true);
        updatePushSettings(
          true,
          pushNotificationSettings.deliveryType,
          "Permission Granted"
        );

        // Register for push notifications
        await PushNotifications.register();

        return true;
      } else {
        setNotificationPermission(false);
        updatePushSettings(
          false,
          pushNotificationSettings.deliveryType,
          "Permission Denied"
        );

        if (
          pushResult.receive === "denied" ||
          localResult.display === "denied"
        ) {
          setToastMessage(
            "Notification permissions denied. Please enable them in Settings to receive notifications."
          );
          setShowToast(true);

          // Show modal to guide user to settings
          setTimeout(() => {
            navigateToSystemSettings();
          }, 2000);
        }

        return false;
      }
    } catch (error) {
      console.error("Error requesting notification permissions:", error);
      setNotificationPermission(false);
      updatePushSettings(
        false,
        pushNotificationSettings.deliveryType,
        "Permission Error"
      );
      setToastMessage("Error requesting permissions. Please try again.");
      setShowToast(true);
      return false;
    }
  };

  const saveSettings = async (settings: TherapySettings) => {
    const data = createSaveData(
      userId,
      settings,
      pushNotificationSettings.enabled
    );
    try {
      await saveNotificationsSettings(data);
      if (storage && isStorageReady) {
        await ionicStorage(storage, "notifications", data);
      }

      // Schedule notifications after saving settings
      if (settings.dailyReminder.enabled && settings.dailyReminder.time) {
        // Only schedule if we have permission or if we can get permission
        if (notificationPermission || Capacitor.isNativePlatform()) {
          await handleNotification(settings.dailyReminder.time);
        } else {
          console.log(
            "Skipping notification scheduling - no permission and not on native platform"
          );
        }
      }
    } catch (error) {
      console.error(
        "Error saving notification settings:",
        JSON.stringify(error)
      );
      setToastMessage("Error saving notification settings");
      setShowToast(true);
    }
  };

  const navigateToSystemSettings = async () => {
    if (Capacitor.isNativePlatform()) {
      try {
        if (platform === "android") {
          // For Android, open app-specific notification settings
          await BleClient.openAppSettings();
        } else if (platform === "ios") {
          // For iOS, open app settings
          await BleClient.openAppSettings();
        }
      } catch (error) {
        console.error("Error opening notification settings:", error);
        setToastMessage(
          "Could not open settings. Please manually enable notifications in your device settings."
        );
        setShowToast(true);
      }
    } else {
      setShowDownloadPrompt(true);
    }
  };

  // EVENT HANDLERS
  const handleBack = () => {
    router.push("/settings");
  };
  
  const handleCloseModal = () => {
    setShowGoalModal(false);
    setShowCustomModal(false);
    setCurrentModalType(null);
    setEditingItem(null);
  };

  const openModal = (section: string, item: NotificationItem) => {
    setEditingItem(item);

    if (section === "PUSH NOTIFICATIONS") {
      if (item.title === "Notifications") {
        // Check if permissions are granted before opening modal
        if (!notificationPermission) {
          // Request permission first
          requestNotificationPermission();
          return;
        }
      }
      setCurrentModalType("pushSettings");
      setShowCustomModal(true);
    } else if (section === "MY THERAPY NOTIFICATIONS") {
      if (item.title === "Daily Reminder") {
        // Check if permissions are granted before allowing daily reminder setup
        // if (!notificationPermission) {
        //   requestNotificationPermission();
        //   return;
        // }
        setCurrentModalType("dailyReminder");
        const timeData = syncTimePickerWithSettings(
          therapySettings.dailyReminder.time
        );
        if (timeData) {
          setHrs(timeData.hrs);
          setMinutes(timeData.minutes);
          setMeridian(timeData.meridian);
        }
        setShowCustomModal(true);
      } else if (item.title === "Missed Days") {
        setCurrentModalType("missedDays");
        setShowGoalModal(true);
      } else if (item.title === "Pressure Thresholds") {
        setCurrentModalType("pressureThresholds");
        setShowGoalModal(true);
      }
    } else if (section === "NOTIFICATIONS FOR USERS I'M FOLLOWING") {
      const user = followingList.find((u) => u.name === item.title);
      const FollowId = followingList.find((u) => u.name === item.title)?.id;
      if (user) {
        router.push(
          `/settings/notification/preferences/${FollowId}?user=${item.title}`
        );
      }
    }
  };

  const handleGoalSave = async (updatedGoal: any) => {
    handleCloseModal();
    let updatedSettings = { ...therapySettings };

    if (currentModalType === "dailyReminder") {
      if (isReminderEnabled) {
        updatedSettings = {
          ...updatedSettings,
          dailyReminder: {
            ...updatedSettings.dailyReminder,
            enabled: isReminderEnabled,
            time: updatedGoal || updatedSettings.dailyReminder.time,
          },
        };
      } else {
        updatedSettings = {
          ...updatedSettings,
          dailyReminder: {
            ...updatedSettings.dailyReminder,
            enabled: false,
          },
        };
        await clearLocalNotifications();
      }
      setTherapySettings(updatedSettings);
      await saveSettings(updatedSettings);
    } else if (currentModalType === "missedDays") {
      const newCount =
        updatedGoal.target_count ?? therapySettings.missedDays.count;
      const newEnabled =
        updatedGoal.isActive ?? therapySettings.missedDays.enabled;

      updatedSettings = {
        ...updatedSettings,
        missedDays: {
          count: newCount,
          enabled: newEnabled,
        },
      };

      setTherapySettings(updatedSettings);
      setMissedDaysGoal((prev: any) => ({
        ...prev,
        target_count: newCount,
        isActive: newEnabled,
      }));

      await saveSettings(updatedSettings);
    } else if (currentModalType === "pressureThresholds") {
      const newValue =
        updatedGoal.target_count ?? therapySettings.pressureThresholds.value;
      const newEnabled =
        updatedGoal.isActive ?? therapySettings.pressureThresholds.enabled;

      updatedSettings = {
        ...updatedSettings,
        pressureThresholds: {
          value: newValue,
          enabled: newEnabled,
        },
      };

      setTherapySettings(updatedSettings);
      setPressureThresholdGoal((prev: any) => ({
        ...prev,
        target_count: newValue,
        isActive: newEnabled,
      }));

      await saveSettings(updatedSettings);
    }
  };

  // UPDATE FUNCTIONS
  const updatePushSettings = (
    enabled: boolean,
    deliveryType: string = pushNotificationSettings.deliveryType,
    source?: string
  ) => {
    setPushNotificationSettings({
      enabled,
      deliveryType,
    });
  };

  const updateDailyReminder = (enabled: boolean, time?: string) => {
    // setTherapySettings((prev: any) => ({
    //   ...prev,
    //   dailyReminder: {
    //     ...prev.dailyReminder,
    //     enabled,
    //     time: time || prev.dailyReminder.time,
    //   },
    // }));
  };

  const updateFollowingSettings = (userId: number, enabled: boolean) => {
    setFollowingList((prev) =>
      prev.map((user) => (user.id === userId ? { ...user, enabled } : user))
    );
  };

  // NOTIFICATION HANDLER - ENHANCED VERSION
  const handleNotification = async (reminderTime?: string) => {
    try {
      // Check if we're on a native platform
      if (!Capacitor.isNativePlatform()) {
        console.log("Local notifications not available on web platform");
        setToastMessage(
          "Notifications are not available on web. Please use the mobile app."
        );
        setShowToast(true);
        return;
      }
      if (isPlatform("android")) {
        const isExactNotificationEnabled =
          await LocalNotifications.checkExactNotificationSetting();
        if (!isExactNotificationEnabled) {
          await LocalNotifications.changeExactNotificationSetting();
        }
      }

      // Check notification permissions first
      const localPermissions = await LocalNotifications.checkPermissions();
      if (localPermissions.display !== "granted") {
        console.log(
          "Local notification permission not granted:",
          localPermissions
        );
        const hasPermission = await requestNotificationPermission();
        if (!hasPermission) {
          return;
        }
      }

      const currentTime = reminderTime || therapySettings.dailyReminder.time;

      if (!currentTime) {
        console.log("No reminder time set");
        setToastMessage("Please set a reminder time first.");
        setShowToast(true);
        return;
      }

      // Parse the time - handle different formats
      let hours: number, minutes: number;

      if (currentTime.includes(":")) {
        const timeParts = currentTime.split(":");
        hours = parseInt(timeParts[0], 10);
        minutes = parseInt(timeParts[1], 10);
      } else {
        // Handle other time formats if needed
        console.error("Invalid time format:", currentTime);
        setToastMessage("Invalid time format. Please set the time again.");
        setShowToast(true);
        return;
      }

      if (
        isNaN(hours) ||
        isNaN(minutes) ||
        hours < 0 ||
        hours > 23 ||
        minutes < 0 ||
        minutes > 59
      ) {
        console.error("Invalid time values:", { hours, minutes, currentTime });
        setToastMessage("Invalid time values. Please set a valid time.");
        setShowToast(true);
        return;
      }

      // Cancel existing notifications first
      try {
        const existingNotifications = await LocalNotifications.getPending();
        if (existingNotifications.notifications.length > 0) {
          const notificationIds = existingNotifications.notifications.map(
            (n) => ({ id: n.id })
          );
          await LocalNotifications.cancel({ notifications: notificationIds });
          console.log(
            `Cancelled ${notificationIds.length} existing notifications`
          );
        }
      } catch (cancelError) {
        console.warn("Error cancelling existing notifications:", cancelError);
        // Continue anyway - this shouldn't block new scheduling
      }

      const notifications: LocalNotificationSchema[] = [];
      const now = new Date();

      const scheduleDate = new Date();
      scheduleDate.setDate(now.getDate());
      scheduleDate.setHours(hours, minutes, 0, 0);
      if (scheduleDate.getTime() <= new Date().getTime()) {
        scheduleDate.setDate(scheduleDate.getDate() + 1);
      }
      let uniqueId: number;
      if (platform === "android") {
        // Generate a valid positive Java int (0 to 2147483647)
        uniqueId = Math.floor(Math.random() * 2147483647);
      } else {
        uniqueId = Date.now();
      }
      // schedule daily notifications (every day from now)
      notifications.push({
        schedule: {
          on: {
            hour: scheduleDate.getHours(),
            minute: scheduleDate.getMinutes(),
          },
          allowWhileIdle: true,
          repeats: true,
        },
        title: "Daily Reminder",
        body: "It's time for BiWaze® therapy!",
        id: uniqueId, // Use higher IDs to avoid conflicts
        sound: "default",
        attachments: undefined,
        actionTypeId: "",
        extra: {
          type: "daily-reminder",
          scheduledTime: currentTime,
        },
      });

      try {
        await LocalNotifications.schedule({
          notifications: notifications,
        });
        console.log(`Scheduled notifications`);
      } catch (error) {
        console.error(`Error scheduling notifications:`, error);
      }
      const pendingNotifications = await LocalNotifications.getPending();
      if (notifications.length > 0) {
        console.log(
          `Successfully scheduled notifications, pending:`,
          pendingNotifications
        );
        setToastMessage(
          `Daily reminders scheduled for ${formatTime(
            currentTime
          )} notifications`
        );
        setShowToast(true);

        // Update notification permission state
        setNotificationPermission(true);
      } else {
        throw new Error("Failed to schedule any notifications");
      }
    } catch (error) {
      console.error("Error scheduling notifications:", error);

      let errorMessage = "Error scheduling notifications";

      if (error instanceof Error) {
        if (error.message.includes("permission")) {
          errorMessage =
            "Notification permission required. Please enable notifications in settings.";
        } else if (error.message.includes("limit")) {
          errorMessage = "Too many notifications scheduled. Please try again.";
        } else {
          errorMessage = `Error scheduling notifications: ${error.message}`;
        }
      }

      setToastMessage(errorMessage);
      setShowToast(true);

      // If it's a permission error, try to request permission
      if (errorMessage.includes("permission")) {
        setTimeout(() => {
          requestNotificationPermission();
        }, 2000);
      }
    }
  };
  // EFFECTS
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      // Check permissions on component mount
      checkPushPermission();
    }
  }, []);

  // FIXED: Proper listener cleanup
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const setupListener = async () => {
      try {
        appStateListenerRef.current = await App.addListener(
          "appStateChange",
          (state) => {
            if (state.isActive) {
              setTimeout(checkPushPermission, 300);
            }
          }
        );
      } catch (error) {
        console.error("Error setting up app state listener:", error);
      }
    };

    setupListener();
    checkPushPermission();

    return () => {
      if (appStateListenerRef.current) {
        appStateListenerRef.current.remove();
        appStateListenerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const resp = syncTimePickerWithSettings(therapySettings.dailyReminder.time);
    if (resp) {
      setHrs(resp.hrs);
      setMinutes(resp.minutes);
      setMeridian(resp.meridian);
    }
    setIsReminderEnabled(therapySettings.dailyReminder.enabled);
  }, [therapySettings.dailyReminder]);

  useEffect(() => {
    const fetchStoredDataAndCheckPermission = async () => {
      if (storage && isStorageReady) {
        try {
          const storedData = await getIonicStorage(storage, "notifications");
          if (storedData?.is_daily_reminder_enabled) {
            // Check both push and local notification permissions
            if (Capacitor.isNativePlatform()) {
              const pushPermissions =
                await PushNotifications.checkPermissions();
              const localPermissions =
                await LocalNotifications.checkPermissions();

              const hasPermissions =
                pushPermissions.receive === "granted" &&
                localPermissions.display === "granted";

              if (hasPermissions) {
                setNotificationPermission(true);
              } else {
                console.log(
                  "Push Notification Permission Status:",
                  pushPermissions,
                  localPermissions
                );
                setNotificationPermission(false);
                setShowToast(true);
                setToastMessage(
                  "Push Notification Permission Denied. You will not receive notifications."
                );
              }
            }
          }
        } catch (error) {
          console.error("Error checking notification permissions:", error);
        }
      }
    };
    fetchStoredDataAndCheckPermission();
  }, [storage, isStorageReady]);

  useEffect(() => {
    const fetchFollowingList = async () => {
      try {
        if (storage && isStorageReady) {
          const storedData = await getIonicStorage(storage, "following");
          if (storedData) {
            const formattedList: User[] = (storedData || []).map(
              (item: any) => {
                // Get full name or fallback to other identifiers
                const firstName = item.profile?.first_name?.trim() || "";
                const lastName = item.profile?.last_name?.trim() || "";

                // Build full name if either first or last name exists
                let displayName = "";
                if (firstName || lastName) {
                  displayName = `${firstName} ${lastName}`.trim();
                }

                // Fallback chain: full name → username → email → phone
                const name =
                  displayName ||
                  item.profile?.username ||
                  item.profile?.email ||
                  item.profile?.phone ||
                  "Unknown User";

                return {
                  id: item.profile?.id || 0,
                  name: name,
                  enabled: true,
                };
              }
            );
            setFollowingList(formattedList);
          }
        }

        const response = await getMyFollowing();
        const formattedList: User[] = (response || []).map((item: any) => {
          // Get full name or fallback to other identifiers
          const firstName = item.profile?.first_name?.trim() || "";
          const lastName = item.profile?.last_name?.trim() || "";

          // Build full name if either first or last name exists
          let displayName = "";
          if (firstName || lastName) {
            displayName = `${firstName} ${lastName}`.trim();
          }

          // Fallback chain: full name → username → email → phone
          const name =
            displayName ||
            item.profile?.username ||
            item.profile?.email ||
            item.profile?.phone ||
            "Unknown User";

          return {
            id: item.profile?.id || 0,
            name: name,
            enabled: true,
          };
        });
        setFollowingList(formattedList);
      } catch (error) {
        console.error("Failed to fetch following users:", error);
        setFollowingList([]);
      }
    };

    fetchFollowingList();
  }, [storage, isStorageReady]);

  useEffect(() => {
    const fetchNotificationData = async () => {
      try {
        checkPushPermission();
        if (storage && isStorageReady) {
          const storedData = await getIonicStorage(storage, "notifications");
          if (storedData) {
            const {
              missedDaysGoalData,
              pressureThresholdGoalData,
              therapySettings: newTherapySettings,
            } = createGoalDataFromResponse(storedData);

            setMissedDaysGoal(missedDaysGoalData);
            setPressureThresholdGoal(pressureThresholdGoalData);
            setTherapySettings((prev: any) => ({
              ...prev,
              ...newTherapySettings,
            }));
          }
        }
        const response = await retrieveNotificationsSettings();

        if (response.data.set_by) {
          setUserId(response.data.set_by);
        }

        const goalData = response?.data || response;

        if (goalData) {
          const {
            missedDaysGoalData,
            pressureThresholdGoalData,
            therapySettings: newTherapySettings,
          } = createGoalDataFromResponse(goalData);

          setMissedDaysGoal(missedDaysGoalData);
          setPressureThresholdGoal(pressureThresholdGoalData);
          setTherapySettings((prev: any) => ({
            ...prev,
            ...newTherapySettings,
          }));
        }
        if (storage && isStorageReady) {
          await ionicStorage(storage, "notifications", response.data);
        }
      } catch (error) {
        let prevData;
        if (storage && isStorageReady) {
          try {
            prevData = await getIonicStorage(storage, "notifications");
            if (prevData) {
              setMissedDaysGoal(prevData.missedDaysGoalData);
              setPressureThresholdGoal(prevData.pressureThresholdGoalData);
            }
          } catch (storageError) {
            console.error("Error reading from storage:", storageError);
          }
        } else {
          console.error("Failed to fetch notification data:", error);
          const defaultData = getDefaultGoalData();
          setMissedDaysGoal(defaultData.missedDaysGoalData);
          setPressureThresholdGoal(defaultData.pressureThresholdGoalData);
        }
      }
    };

    if (storage && isStorageReady) {
      fetchNotificationData();
    }
  }, [storage, isStorageReady]);

  // RENDER FUNCTIONS
  const renderNotificationItem = (
    title: string,
    subtitle: string,
    section: string,
    systemSettings = false
  ) => (
    <div
      className="flex justify-between items-center p-4 border-b border-gray-200 last:border-b-0 cursor-pointer"
      onClick={() =>
        systemSettings
          ? navigateToSystemSettings()
          : openModal(section, { title, subtitle })
      }
    >
      <div>
        <h3 className="text-lg font-normal text-black mb-1">
          {title.length > 20 ? title.slice(0, 20) + "..." : title}
        </h3>
        <p className="text-gray-500">
          {subtitle}
          {/* {(title === "Notifications" || title === "Daily Reminder") &&
            !notificationPermission &&
            platform !== "web" && (
              <span className="text-orange-500 ml-2">(Tap to enable)</span>
            )} */}
        </p>
      </div>
      <div className="flex items-center">
        {/* {(title === "Notifications" || title === "Daily Reminder") &&
          !notificationPermission &&
          platform !== "web" && (
            <span className="text-orange-500 mr-2 text-sm">!</span>
          )} */}
        <button className="flex items-center justify-center">
          <IonIcon icon={chevronForward} style={{ fontSize: "24px" }} />
        </button>
      </div>
    </div>
  );

  const renderNotificationSection = (
    title: string,
    items: NotificationItem[]
  ) => (
    <>
      <div className="section-header">
        <div>
          <div className="settings-title !mt-0 !ms-4">{title}</div>
        </div>
      </div>
      <div className="bg-white rounded-xl overflow-hidden shadow-[0_16px_64px_rgba(0,0,0,0.1)] mb-4">
        {items.map((item, index) => (
          <>
            <div key={index}>
              {platform === "web" && item.title === "Notifications" ? (
                <div className="p-4 text-gray-500">
                  Push notifications are not available on web.
                </div>
              ) : (
                renderNotificationItem(
                  item.title,
                  item.subtitle,
                  title,
                  item.title === "Notifications"
                )
              )}
            </div>
            <hr className="border-gray-200 w-[98%] justify-self-center" />
          </>
        ))}
      </div>
    </>
  );

  // GENERATE DYNAMIC DATA
  const pushNotifications: NotificationItem[] = [
    {
      title: "Notifications",
      subtitle: `${pushNotificationSettings.enabled ? "On" : "Off"}`,
    },
  ];

  const therapyNotifications: NotificationItem[] = [
    {
      title: "Daily Reminder",
      subtitle: therapySettings.dailyReminder.enabled
        ? formatTime(therapySettings.dailyReminder.time)
        : "Off",
    },
    {
      title: "Missed Days",
      subtitle: therapySettings.missedDays.enabled
        ? `${therapySettings.missedDays.count} day${
            therapySettings.missedDays.count !== 1 ? "s  in a row" : ""
          }`
        : "Off",
    },
    // {
    //   title: "Pressure Thresholds",
    //   subtitle: therapySettings.pressureThresholds.enabled
    //     ? `± ${therapySettings.pressureThresholds.value} cmH₂O`
    //     : "Off",
    // },
  ];

  const followingNotifications = followingList.map((user) => ({
    title: user.name,
    subtitle: "",
  }));

  // MAIN RENDER
  return (
    <>
      <IonPage>
        {/* <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={4000}
          position="top"
          color="primary"
          buttons={[
            {
              text: "Close",
              role: "cancel",
            },
          ]}
        /> */}
        <Header
          showGreeting={false}
          showPageTitle={true}
          showUserIcon={false}
          pageTitle="Notifications"
          showBackButton={true}
          onBackButtonClick={handleBack}
        />
        <IonContent
          className="ion-padding"
          style={{ "--background": "#F7F5FA" }}
        >
          <div className="flex flex-col">
            {renderNotificationSection("PUSH NOTIFICATIONS", pushNotifications)}
            {renderNotificationSection(
              "MY THERAPY NOTIFICATIONS",
              therapyNotifications
            )}
            {renderNotificationSection(
              "NOTIFICATIONS FOR USERS I'M FOLLOWING",
              followingNotifications
            )}
          </div>

          {/* Render Goal Modal */}
          {renderGoalModal({
            showGoalModal,
            currentModalType,
            missedDaysGoal,
            pressureThresholdGoal,
            handleCloseModal,
            handleGoalSave,
          })}

          {/* Render Custom Modal */}
          <div style={{ paddingBottom: "60px" }}>
            {renderCustomModal({
              showCustomModal,
              currentModalType,
              handleCloseModal,
              handleGoalSave,
              navigateToSystemSettings,
              isReminderEnabled,
              setIsReminderEnabled,
              updateDailyReminder,
              hrs,
              setHrs,
              minutes,
              setMinutes,
              meridian,
              setMeridian,
              pushNotificationSettings,
              updatePushSettings,
              followingList,
              updateFollowingSettings,
            })}
          </div>
        </IonContent>
      </IonPage>
      {/* Download app prompt modal */}
      <IonModal
        isOpen={showDownloadPrompt}
        onDidDismiss={() => setShowDownloadPrompt(false)}
        style={{
          "--border-radius": "20px",
          "--width": "95%",
          "--height": "auto",
          "--background": "#FFFFFF",
        }}
      >
        <div className="flex flex-col items-center p-4">
          <h3 className="text-xl font-bold mb-4">Download Our App</h3>
          <p className="text-center mb-4">
            To manage system notifications, please download our mobile app from
            the App Store or Google Play.
          </p>
          <div className="flex gap-3 w-full">
            <IonButton
              expand="block"
              fill="outline"
              href="https://apps.apple.com/app/yourapp"
              target="_blank"
              className="flex-1"
            >
              App Store
            </IonButton>
            <IonButton
              expand="block"
              fill="outline"
              href="https://play.google.com/store/apps/details?id=com.yourapppackage"
              target="_blank"
              className="flex-1"
            >
              Google Play
            </IonButton>
          </div>
          <IonButton
            expand="block"
            fill="clear"
            onClick={() => setShowDownloadPrompt(false)}
            className="mt-3"
          >
            Close
          </IonButton>
        </div>
      </IonModal>
    </>
  );
};

export default MyNotifications;
